/**
 * Equipment + Projects inputs (PRD v2 §6.7–6.8). A leaf module: it takes the idempotency
 * envelope from the spine and nothing imports it back.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { PROJECT_STATES } from '../statemachines/project';

const text = (max: number) => z.string().trim().max(max).optional().or(z.literal(''));
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-07-30')
  .optional()
  .or(z.literal(''));

/** What kind of thing it is — labels a shop already says out loud, not a taxonomy. */
export const EQUIPMENT_CATEGORIES = [
  'hvac',
  'water_heater',
  'boiler',
  'plumbing',
  'electrical',
  'appliance',
  'other',
] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

export const equipmentCreateInput = idempotency.extend({
  propertyId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  category: z.enum(EQUIPMENT_CATEGORIES).optional(),
  make: text(80),
  model: text(80),
  serial: text(80),
  installedOn: isoDate,
  warrantyExpiresOn: isoDate,
  laborWarrantyExpiresOn: isoDate,
  locationNote: text(200),
  notes: text(2000),
});
export type EquipmentCreateInput = z.infer<typeof equipmentCreateInput>;

export const equipmentUpdateInput = idempotency.extend({
  equipmentId: z.string().uuid(),
  name: z.string().trim().min(1).max(120).optional(),
  category: z.enum(EQUIPMENT_CATEGORIES).nullable().optional(),
  make: text(80),
  model: text(80),
  serial: text(80),
  installedOn: isoDate,
  warrantyExpiresOn: isoDate,
  laborWarrantyExpiresOn: isoDate,
  locationNote: text(200),
  notes: text(2000),
  /** Retired, not deleted: the unit came out, but its history is why the shop kept records. */
  active: z.boolean().optional(),
});
export type EquipmentUpdateInput = z.infer<typeof equipmentUpdateInput>;

export const equipmentIdInput = idempotency.extend({
  equipmentId: z.string().uuid(),
});
export type EquipmentIdInput = z.infer<typeof equipmentIdInput>;

/** Which units this job touched — the link that turns jobs into a per-unit history. */
export const jobEquipmentInput = idempotency.extend({
  jobId: z.string().uuid(),
  equipmentId: z.string().uuid(),
});
export type JobEquipmentInput = z.infer<typeof jobEquipmentInput>;

// ── Projects (§6.8) ──────────────────────────────────────────────────────────

export const projectCreateInput = idempotency.extend({
  name: z.string().trim().min(1).max(160),
  customerId: z.string().uuid(),
  propertyId: z.string().uuid().nullable().optional(),
  description: text(4000),
  startsOn: isoDate,
  targetEndOn: isoDate,
});
export type ProjectCreateInput = z.infer<typeof projectCreateInput>;

export const projectUpdateInput = idempotency.extend({
  projectId: z.string().uuid(),
  name: z.string().trim().min(1).max(160).optional(),
  propertyId: z.string().uuid().nullable().optional(),
  description: text(4000),
  startsOn: isoDate,
  targetEndOn: isoDate,
});
export type ProjectUpdateInput = z.infer<typeof projectUpdateInput>;

export const projectTransitionInput = idempotency.extend({
  projectId: z.string().uuid(),
  via: z.enum([
    'project.start',
    'project.hold',
    'project.resume',
    'project.close',
    'project.cancel',
    'project.reopen',
  ]),
});
export type ProjectTransitionInput = z.infer<typeof projectTransitionInput>;

/**
 * Attach (or detach) a piece of work to a project. One mutation for all five kinds because
 * it is one act — "this belongs to that build" — and five near-identical endpoints is how a
 * codebase grows five slightly different answers.
 */
export const PROJECT_LINKABLE = [
  'request',
  'quote',
  'job',
  'invoice',
  'purchase_order',
] as const;
export type ProjectLinkable = (typeof PROJECT_LINKABLE)[number];

export const projectLinkInput = idempotency.extend({
  entityType: z.enum(PROJECT_LINKABLE),
  entityId: z.string().uuid(),
  /** Null detaches. The work survives — only the grouping goes away. */
  projectId: z.string().uuid().nullable(),
});
export type ProjectLinkInput = z.infer<typeof projectLinkInput>;

export const projectStatusValues = PROJECT_STATES;
